import { type MessageKey } from '@ecom/i18n';

/** The CLDR plural categories `Intl.PluralRules` returns, as key suffixes. */
const SUFFIX: Record<Intl.LDMLPluralRule, string> = {
  zero: 'Zero',
  one: 'One',
  two: 'Two',
  few: 'Few',
  many: 'Many',
  other: 'Other',
};

type HasAllForms<B extends string> =
  `${B}Zero` | `${B}One` | `${B}Two` | `${B}Few` | `${B}Many` | `${B}Other` extends MessageKey
  ? B
  : never;

/** A message base that exists with all six forms (`…Zero` … `…Other`) in the catalogue — checked when compiling. */
export type PluralBase = {
  [K in MessageKey]: K extends `${infer B}Other` ? HasAllForms<B> : never;
}[MessageKey];

/**
 * The message for a count, by the language's own plural rules (`Intl.PluralRules`), never by `count === 1`. French
 * puts 0 in the singular (« 0 commande »); Arabic has a dual (« طلبان »), a form for 3 to 10 (« 3 طلبات ») and one for
 * 11 and up (« 33 طلبًا »). French and English only use `one` and `other`; their catalogues repeat `other` for the rest.
 */
export function pluralKey(locale: string, count: number, base: PluralBase): MessageKey {
  return `${base}${SUFFIX[new Intl.PluralRules(locale).select(count)]}` as MessageKey;
}
